import { useCallback, useEffect, useRef } from "react";
import { socket } from "../../../common/socket";

export const useTyping = (selectedChatId) => {
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const pendingTypingRef = useRef(null);

  const handleTyping = useCallback((isUserTyping) => {
    if (!selectedChatId) {
      console.log("❌ [TYPING] No selected chat for typing");
      return;
    }

    // Prevent duplicate events
    if (isTypingRef.current === isUserTyping) {
      console.log(`🔄 [TYPING] Duplicate typing event: ${isUserTyping}`, { chatId: selectedChatId });
      return;
    }
    
    isTypingRef.current = isUserTyping;

    console.log(`⌨️ [TYPING] ${isUserTyping ? 'START' : 'STOP'} for chat:`, {
      chatId: selectedChatId,
      previousState: !isUserTyping,
      socketConnected: socket.connected,
      socketId: socket.id,
      timestamp: new Date().toISOString()
    });

    // If socket is not connected, store the typing state and try to reconnect
    if (!socket.connected) {
      console.warn("⚠️ [TYPING] Socket not connected, storing typing state for later");
      pendingTypingRef.current = { isUserTyping, selectedChatId };
      
      // Try to reconnect if not already connecting
      if (!socket.connecting) {
        console.log("🔌 [TYPING] Attempting socket reconnection");
        socket.connect();
      }
      return;
    }

    try {
      // Get current user info for typing events
      let userInfo = null;
      let userId = null;
      try {
        const authRaw = localStorage.getItem("authUser");
        if (authRaw) {
          const auth = JSON.parse(authRaw);
          const user = auth?.user || auth;
          if (user) {
            userId = user._id;
            userInfo = {
              _id: user._id,
              firstname: user.firstname,
              lastname: user.lastname,
              email: user.email,
              profileimg: user.profileimg
            };
          }
        }
      } catch (error) {
        console.warn("❌ [TYPING] Could not get user info:", error);
      }

      if (!userId) {
        console.error("❌ [TYPING] No user ID found for typing event");
        return;
      }

      const typingData = {
        chatId: selectedChatId,
        userId: userId,
        isTyping: isUserTyping,
        user: userInfo
      };

      console.log("📤 [TYPING] Emitting socket event:", {
        event: isUserTyping ? "typing:start" : "typing:stop",
        data: typingData,
        socketId: socket.id,
        connected: socket.connected
      });

      // ✅ Use EXACT backend event names with proper data
      if (isUserTyping) {
        socket.emit("typing:start", typingData);
      } else {
        socket.emit("typing:stop", typingData);
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        console.log("⏰ [TYPING] Clearing previous timeout");
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Set timeout to automatically stop typing after 3 seconds
      if (isUserTyping) {
        console.log("⏰ [TYPING] Setting auto-stop timeout (3s)");
        typingTimeoutRef.current = setTimeout(() => {
          console.log("⏰ [TYPING] Auto-stop typing after timeout", {
            chatId: selectedChatId,
            userId: userId
          });
          isTypingRef.current = false;
          socket.emit("typing:stop", typingData);
          typingTimeoutRef.current = null;
        }, 3000);
      }
    } catch (error) {
      console.error("❌ [TYPING] Error in handleTyping:", error);
      isTypingRef.current = false;
    }
  }, [selectedChatId]);

  const stopTyping = useCallback(() => {
    console.log("🛑 [TYPING] Manual stopTyping called", {
      chatId: selectedChatId,
      wasTyping: isTypingRef.current,
      hasTimeout: !!typingTimeoutRef.current,
      socketConnected: socket.connected
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    
    if (isTypingRef.current && selectedChatId) {
      isTypingRef.current = false;
      
      let userInfo = null;
      let userId = null;
      try {
        const authRaw = localStorage.getItem("authUser");
        if (authRaw) {
          const auth = JSON.parse(authRaw);
          const user = auth?.user || auth;
          if (user) {
            userId = user._id;
            userInfo = {
              _id: user._id,
              firstname: user.firstname,
              lastname: user.lastname,
              email: user.email,
              profileimg: user.profileimg
            };
          }
        }
      } catch (error) {
        console.warn("❌ [TYPING] Could not get user info for stop:", error);
      }

      if (userId && socket.connected) {
        console.log("🛑 [TYPING] Emitting manual typing stop", {
          chatId: selectedChatId,
          userId: userId
        });

        socket.emit("typing:stop", {
          chatId: selectedChatId,
          userId: userId,
          isTyping: false,
          user: userInfo
        });
      } else if (userId) {
        console.warn("⚠️ [TYPING] Socket not connected, cannot send manual stop");
      }
    }
  }, [selectedChatId]);

  // Add socket connection listener to send pending typing events
  useEffect(() => {
    const handleConnect = () => {
      console.log("✅ [TYPING] Socket connected, checking for pending typing events");
      if (pendingTypingRef.current && pendingTypingRef.current.selectedChatId === selectedChatId) {
        console.log("🔄 [TYPING] Sending pending typing event after reconnect");
        handleTyping(pendingTypingRef.current.isUserTyping);
        pendingTypingRef.current = null;
      }
    };

    socket.on("connect", handleConnect);

    return () => {
      socket.off("connect", handleConnect);
    };
  }, [selectedChatId, handleTyping]);

  return { handleTyping, stopTyping, typingTimeoutRef };
};